import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import type { MeetingDetail, SystemStatus } from "../types";
import { LeadCard } from "../components/LeadCard";
import { IconUpload } from "../components/icons";
import {
  fmtBytes,
  fmtClock,
  fmtDateTime,
  fmtTime,
  initials,
  isLiveNow,
  statusBadge,
  syncBadge,
} from "../components/util";

type Tab = "summary" | "transcript" | "log";

export function MeetingDetailPage({
  status,
  onChanged,
  isAdmin,
}: {
  status: SystemStatus | null;
  onChanged: () => void;
  isAdmin: boolean;
}) {
  const { id } = useParams();
  const meetingId = Number(id);
  const navigate = useNavigate();

  const [meeting, setMeeting] = useState<MeetingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [tab, setTab] = useState<Tab>("summary");
  const mediaRef = useRef<HTMLMediaElement>(null);

  const load = useCallback(
    (quiet = false) => {
      if (!quiet) setLoading(true);
      api
        .getMeeting(meetingId)
        .then((m) => {
          setMeeting(m);
          setError("");
        })
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    },
    [meetingId],
  );

  useEffect(() => load(), [load]);

  // Пока конвейер работает — обновляем карточку, чтобы разбор появился сам
  useEffect(() => {
    // Опрашиваем сервер только пока шаг реально идёт. Задачу, отложенную
    // на потом, ждать бессмысленно — иначе страница обновлялась бы часами.
    const now = Date.now();
    const busy =
      meeting?.is_processing ||
      meeting?.pipeline.some(
        (j) =>
          j.status === "running" ||
          (j.status === "queued" && new Date(j.run_at).getTime() <= now),
      );
    if (!busy) return;
    const t = setInterval(() => load(true), 6000);
    return () => clearInterval(t);
  }, [meeting, load]);

  const act = async (fn: () => Promise<unknown>, message: string) => {
    setNotice("");
    setError("");
    try {
      await fn();
      setNotice(message);
      onChanged();
      setTimeout(() => load(true), 600);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // Удаление необратимо и уносит карточку клиента со всей историей —
  // сервер пускает к нему только администратора, кнопки у остальных нет.
  const remove = () => {
    if (!meeting) return;
    const lead = meeting.lead;
    const name = lead
      ? [lead.company, lead.contact_name].filter(Boolean).join(" — ") || meeting.topic
      : meeting.topic;
    const question = lead
      ? `Удалить карточку «${name}»?\n\nВместе с ней удалятся запись разговора, расшифровка, история и задачи. Вернуть её можно будет только из резервной копии.`
      : `Удалить встречу «${name}» вместе с записью и расшифровкой? Это необратимо.`;
    if (!confirm(question)) return;
    api
      .deleteMeeting(meeting.id)
      .then(() => navigate(lead ? "/leads" : "/meetings"))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  };

  const seekTo = (seconds: number) => {
    const v = mediaRef.current;
    if (!v) return;
    v.currentTime = seconds;
    v.play().catch(() => {});
    v.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (loading) {
    return (
      <div className="card">
        <div className="empty">
          <span className="spinner" /> Загружаю…
        </div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="card">
        <div className="empty">
          <h3>Встреча не найдена</h3>
          <p className="muted">{error}</p>
          <Link className="btn" to="/meetings">
            К расписанию
          </Link>
        </div>
      </div>
    );
  }

  const badge = statusBadge(meeting);
  const sync = syncBadge(meeting);
  const live = isLiveNow(meeting);
  const upcoming = meeting.status === "scheduled" || meeting.status === "live";
  // У онлайн-встреч есть видео, у очных — только звук. Плеер нужен в обоих случаях.
  const media =
    meeting.recordings.find((r) => r.kind === "video") ??
    meeting.recordings.find((r) => r.kind === "audio") ??
    null;
  const isVideo = media?.kind === "video";
  // Шаг может уже выполняться, а может ждать своего времени: например,
  // «включить запись» ставится на минуту после начала встречи. Ожидание —
  // это не обработка, и показывать его надо иначе.
  const nowMs = Date.now();
  const isDue = (j: MeetingDetail["pipeline"][number]) =>
    j.status === "running" ||
    (j.status === "queued" && new Date(j.run_at).getTime() <= nowMs);

  const busyJob = meeting.pipeline.find(isDue);
  const waitingJob = meeting.pipeline.find(
    (j) => j.status === "queued" && new Date(j.run_at).getTime() > nowMs,
  );
  const failedJob = meeting.pipeline.find((j) => j.status === "failed");

  return (
    <>
      <div className="page-head">
        <div style={{ minWidth: 0 }}>
          <div className="row small muted" style={{ marginBottom: 4 }}>
            <Link to="/meetings">Расписание</Link> <span>/</span>
            <span>{fmtDateTime(meeting.start_time)}</span>
          </div>
          <h1>{meeting.topic}</h1>
          <div className="row">
            <span className={`badge ${badge.cls}`}>
              {live && <span className="dot pulse" />}
              {badge.label}
            </span>
            {sync && (
              <span className={`badge ${sync.cls}`} title={sync.title}>
                {sync.label}
              </span>
            )}
            <span className="badge">{meeting.duration_min} мин</span>
            <span className="badge">{meeting.timezone}</span>
            {meeting.auto_recording === "cloud" && (
              <span className="badge">● автозапись в облако</span>
            )}
          </div>
        </div>

        <div className="row nowrap">
          {upcoming && meeting.join_url && (
            <a
              className="btn btn-primary"
              href={meeting.join_url}
              target="_blank"
              rel="noreferrer"
            >
              Подключиться
            </a>
          )}
          {upcoming && meeting.start_url && (
            <a
              className="btn"
              href={meeting.start_url}
              target="_blank"
              rel="noreferrer"
              title="Ссылка для ведущего"
            >
              Начать как ведущий
            </a>
          )}
          {meeting.status !== "canceled" && (
            <Link className="btn" to={`/meetings/${meeting.id}/edit`}>
              Изменить
            </Link>
          )}
        </div>
      </div>

      {notice && <div className="banner blue">{notice}</div>}
      {error && <div className="banner red">{error}</div>}
      {meeting.sync_status === "error" && meeting.sync_error && (
        <div className="banner red">
          <span>⚠️</span>
          <div>
            <strong>Zoom вернул ошибку при создании встречи</strong>
            <span className="mono small">{meeting.sync_error}</span>
          </div>
        </div>
      )}
      {busyJob && (
        <div className="banner blue">
          <span className="spinner" />
          <div>
            <strong>Идёт обработка: {jobLabel(busyJob.type)}</strong>
            Страница обновится сама, как только шаг завершится.
          </div>
        </div>
      )}
      {waitingJob && !busyJob && (
        <div className="banner">
          <span>🕒</span>
          <div>
            <strong>
              Запланировано: {jobLabel(waitingJob.type)} —{" "}
              {new Date(waitingJob.run_at).toLocaleString("ru-RU", {
                day: "numeric",
                month: "long",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </strong>
            Сервис сделает это сам в нужный момент, ничего нажимать не надо.
          </div>
        </div>
      )}
      {failedJob && !busyJob && (
        <div className="banner amber">
          <span>⚠️</span>
          <div>
            <strong>Шаг «{jobLabel(failedJob.type)}» не удался</strong>
            <span className="mono small">{failedJob.last_error}</span>
          </div>
        </div>
      )}

      <div className="detail-grid">
        <div>
          {media ? (
            <div className="card">
              <div className="card-head">
                <h3>{isVideo ? "Запись встречи" : "Аудиозапись"}</h3>
                <div className="row">
                  <span className="muted small">
                    {fmtBytes(media.file_size)}
                    {media.duration_sec > 0 && ` · ${fmtClock(media.duration_sec)}`}
                  </span>
                  <a className="btn sm" href={media.download_url}>
                    Скачать
                  </a>
                </div>
              </div>
              <div className="card-body">
                {isVideo ? (
                  <video
                    ref={mediaRef as React.RefObject<HTMLVideoElement>}
                    controls
                    preload="metadata"
                    src={media.stream_url}
                  >
                    Ваш браузер не умеет воспроизводить видео.
                  </video>
                ) : (
                  <audio
                    ref={mediaRef as React.RefObject<HTMLAudioElement>}
                    controls
                    preload="metadata"
                    src={media.stream_url}
                    style={{ width: "100%" }}
                  >
                    Ваш браузер не умеет воспроизводить аудио.
                  </audio>
                )}
                <div className="muted small" style={{ marginTop: 8 }}>
                  Файл лежит на этой платформе — ходить во внешние сервисы не нужно.
                </div>
              </div>
            </div>
          ) : meeting.kind === "offline" ? (
            <UploadCard
              meeting={meeting}
              onUploaded={(m) => {
                setMeeting(m);
                setNotice("Запись загружена — расшифровываю.");
              }}
              onError={setError}
            />
          ) : (
            meeting.status === "ended" && (
              <div className="card">
                <div className="empty">
                  <h3>Записи пока нет</h3>
                  <p>
                    Zoom обрабатывает файл после встречи — обычно это занимает
                    несколько минут. Можно проверить прямо сейчас.
                  </p>
                  <button
                    onClick={() =>
                      act(
                        () => api.ingestMeeting(meeting.id),
                        "Проверяю Zoom и забираю запись…",
                      )
                    }
                    disabled={!meeting.zoom_meeting_id}
                  >
                    Забрать запись из Zoom
                  </button>
                </div>
              </div>
            )
          )}

          <div className="card">
            <div className="tabs" style={{ margin: 0, padding: "0 8px" }}>
              <button
                className={tab === "summary" ? "active" : ""}
                onClick={() => setTab("summary")}
              >
                Разбор
              </button>
              <button
                className={tab === "transcript" ? "active" : ""}
                onClick={() => setTab("transcript")}
              >
                Расшифровка
                {meeting.transcript && (
                  <span className="muted small">
                    {" "}
                    · {meeting.transcript.word_count} слов
                  </span>
                )}
              </button>
              <button
                className={tab === "log" ? "active" : ""}
                onClick={() => setTab("log")}
              >
                Хроника
              </button>
            </div>

            {tab === "summary" &&
              (meeting.analysis_profile === "expo" ? (
                meeting.lead ? (
                  <LeadCard
                    lead={meeting.lead}
                    onChanged={() => load(true)}
                    onDelete={isAdmin ? remove : undefined}
                  />
                ) : (
                  <div className="empty">
                    <h3>Карточка клиента ещё не готова</h3>
                    <p>
                      {meeting.has_transcript
                        ? "Расшифровка есть — можно собрать карточку."
                        : "Сначала нужна расшифровка записи."}
                    </p>
                    {meeting.has_transcript && (
                      <button
                        className="primary"
                        onClick={() =>
                          act(
                            () => api.analyzeMeeting(meeting.id, true),
                            "Собираю карточку клиента…",
                          )
                        }
                        disabled={!status?.ai.configured}
                      >
                        Собрать карточку
                      </button>
                    )}
                  </div>
                )
              ) : (
                <SummaryTab
                  meeting={meeting}
                  aiReady={!!status?.ai.configured}
                  onAnalyze={() =>
                    act(
                      () => api.analyzeMeeting(meeting.id, true),
                      "Разбор поставлен в очередь — займёт меньше минуты.",
                    )
                  }
                />
              ))}

            {tab === "transcript" && (
              <TranscriptTab
                meeting={meeting}
                hasVideo={!!media}
                onSeek={seekTo}
                onTranscribe={() =>
                  act(
                    () => api.transcribeMeeting(meeting.id, true),
                    "Расшифровка запущена.",
                  )
                }
              />
            )}

            {tab === "log" && <LogTab meeting={meeting} />}
          </div>
        </div>

        <aside>
          <div className="card">
            <div className="card-head">
              <h3>Подключение</h3>
            </div>
            <div className="card-body">
              {meeting.join_url ? (
                <>
                  <div className="field">
                    <label>Ссылка для участников</label>
                    <div className="row">
                      <input readOnly value={meeting.join_url} />
                      <button
                        className="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(meeting.join_url!);
                          setNotice("Ссылка скопирована");
                        }}
                      >
                        Копировать
                      </button>
                    </div>
                  </div>
                  {meeting.passcode && (
                    <div className="field">
                      <label>Код доступа</label>
                      <div className="mono">{meeting.passcode}</div>
                    </div>
                  )}
                  <div className="muted small">
                    ID встречи: <span className="mono">{meeting.zoom_meeting_id}</span>
                  </div>
                </>
              ) : (
                <>
                  <p className="muted small">
                    Ссылки ещё нет: встреча не заведена в Zoom.
                  </p>
                  <button
                    onClick={() =>
                      act(
                        () => api.syncMeeting(meeting.id),
                        "Ставлю встречу в очередь на создание в Zoom…",
                      )
                    }
                    disabled={!status?.zoom.configured}
                  >
                    Завести в Zoom сейчас
                  </button>
                  {!status?.zoom.configured && (
                    <div className="muted small" style={{ marginTop: 8 }}>
                      Сначала заполните ключи Zoom в <span className="mono">.env</span>.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Участники</h3>
              <span className="muted small">{meeting.participants.length}</span>
            </div>
            <div className="card-body">
              {meeting.participants.length === 0 ? (
                <p className="muted small">Никого не позвали.</p>
              ) : (
                <ul className="list-clean">
                  {meeting.participants.map((p) => {
                    const att = meeting.attendance.find(
                      (a) => a.email.toLowerCase() === p.email.toLowerCase(),
                    );
                    return (
                      <li key={p.id} className="row" style={{ gap: 10 }}>
                        <span
                          className={`avatar ${p.kind === "external" ? "external" : ""}`}
                        >
                          {initials(p.name)}
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <div>{p.name}</div>
                          <div className="muted small">{p.email}</div>
                        </span>
                        {att && (
                          <span className="badge green" title="По данным Zoom">
                            был(а)
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {meeting.attendance.length > 0 && (
                <details style={{ marginTop: 12 }}>
                  <summary className="muted small">
                    Кто и когда заходил ({meeting.attendance.length})
                  </summary>
                  <ul className="list-clean small" style={{ marginTop: 8 }}>
                    {meeting.attendance.map((a) => (
                      <li key={a.id}>
                        <strong>{a.name || a.email || "без имени"}</strong>
                        <div className="muted">
                          {fmtTime(a.joined_at)} → {fmtTime(a.left_at)}
                        </div>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </div>

          {meeting.agenda && (
            <div className="card">
              <div className="card-head">
                <h3>Повестка</h3>
              </div>
              <div className="card-body summary-block">{meeting.agenda}</div>
            </div>
          )}

          <div className="card">
            <div className="card-head">
              <h3>Действия</h3>
            </div>
            <div className="card-body">
              <div className="row">
                {meeting.zoom_meeting_id && (
                  <button
                    className="sm"
                    onClick={() =>
                      act(
                        () => api.ingestMeeting(meeting.id),
                        "Проверяю Zoom и забираю запись…",
                      )
                    }
                  >
                    Забрать запись
                  </button>
                )}
                {(meeting.has_recording || meeting.has_transcript) && (
                  <button
                    className="sm"
                    onClick={() =>
                      act(
                        () => api.transcribeMeeting(meeting.id, true),
                        "Расшифровка запущена.",
                      )
                    }
                  >
                    Перерасшифровать
                  </button>
                )}
                {meeting.has_transcript && (
                  <button
                    className="sm"
                    onClick={() =>
                      act(
                        () => api.analyzeMeeting(meeting.id, true),
                        "Разбор поставлен в очередь.",
                      )
                    }
                    disabled={!status?.ai.configured}
                  >
                    Разобрать заново
                  </button>
                )}
                {meeting.status !== "canceled" && upcoming && (
                  <button
                    className="sm danger"
                    onClick={() => {
                      if (confirm("Отменить встречу и удалить её из Zoom?")) {
                        act(() => api.cancelMeeting(meeting.id), "Встреча отменена.");
                      }
                    }}
                  >
                    Отменить встречу
                  </button>
                )}
                {isAdmin && (
                  <button className="sm danger" onClick={remove}>
                    {meeting.lead ? "Удалить карточку" : "Удалить"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}

function jobLabel(type: string): string {
  const map: Record<string, string> = {
    "meeting.sync": "создание встречи в Zoom",
    "meeting.remove": "удаление встречи из Zoom",
    "recording.ensure": "запуск записи",
    "meeting.ingest": "загрузка записи",
    "meeting.transcribe": "расшифровка",
    "meeting.analyze": "разбор через Claude",
  };
  return map[type] ?? type;
}

function SummaryTab({
  meeting,
  aiReady,
  onAnalyze,
}: {
  meeting: MeetingDetail;
  aiReady: boolean;
  onAnalyze: () => void;
}) {
  const s = meeting.summary;

  if (!s) {
    return (
      <div className="empty">
        <h3>Разбора пока нет</h3>
        {!meeting.has_transcript ? (
          <p>
            Сначала нужна расшифровка. Она появится автоматически после того, как
            Zoom отдаст запись.
          </p>
        ) : !aiReady ? (
          <p>
            Расшифровка готова, но <span className="mono">ANTHROPIC_API_KEY</span>{" "}
            не задан. Добавьте ключ в <span className="mono">.env</span>,
            перезапустите сервер — задача из очереди выполнится сама.
          </p>
        ) : (
          <>
            <p>Расшифровка готова — можно получить выжимку и договорённости.</p>
            <button className="primary" onClick={onAnalyze}>
              Разобрать через Claude
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="card-body">
      {s.headline && <h2>{s.headline}</h2>}

      <div className="row small muted" style={{ marginBottom: 14 }}>
        <span>модель {s.model}</span>
        <span>·</span>
        <span>
          {s.input_tokens.toLocaleString("ru-RU")} вх. /{" "}
          {s.output_tokens.toLocaleString("ru-RU")} исх. токенов
        </span>
        <span>·</span>
        <a href={`/api/meetings/${meeting.id}/summary.md`} download>
          Скачать Markdown
        </a>
      </div>

      <h3>Кратко</h3>
      <p className="summary-block">{s.summary}</p>

      {s.decisions.length > 0 && (
        <>
          <h3>Договорённости</h3>
          <ul className="list-clean">
            {s.decisions.map((d, i) => (
              <li key={i}>
                <strong>{d.statement}</strong>
                {d.context && <div className="muted small">{d.context}</div>}
              </li>
            ))}
          </ul>
        </>
      )}

      {s.action_items.length > 0 && (
        <>
          <h3>Задачи</h3>
          <ul className="list-clean">
            {s.action_items.map((t, i) => (
              <li key={i} className="task">
                <span
                  className={`badge ${
                    t.priority === "high"
                      ? "red"
                      : t.priority === "medium"
                        ? "amber"
                        : ""
                  }`}
                >
                  {t.priority === "high"
                    ? "важно"
                    : t.priority === "medium"
                      ? "средне"
                      : "потом"}
                </span>
                <span style={{ flex: 1 }}>
                  <div>{t.task}</div>
                  {(t.owner || t.due) && (
                    <div className="meta">
                      {t.owner && <>Ответственный: {t.owner}</>}
                      {t.owner && t.due && " · "}
                      {t.due && <>Срок: {t.due}</>}
                    </div>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {s.topics.length > 0 && (
        <>
          <h3>Темы</h3>
          <ul className="list-clean">
            {s.topics.map((t, i) => (
              <li key={i}>
                <strong>{t.title}</strong>
                <div className="muted small">{t.detail}</div>
              </li>
            ))}
          </ul>
        </>
      )}

      {s.open_questions.length > 0 && (
        <>
          <h3>Открытые вопросы</h3>
          <ul className="list-clean">
            {s.open_questions.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function TranscriptTab({
  meeting,
  hasVideo,
  onSeek,
  onTranscribe,
}: {
  meeting: MeetingDetail;
  hasVideo: boolean;
  onSeek: (s: number) => void;
  onTranscribe: () => void;
}) {
  const [query, setQuery] = useState("");
  const t = meeting.transcript;

  if (!t) {
    return (
      <div className="empty">
        <h3>Расшифровки пока нет</h3>
        <p>
          {meeting.has_recording
            ? "Запись есть — можно запустить расшифровку вручную."
            : "Расшифровка появится автоматически, как только сервис заберёт запись из Zoom."}
        </p>
        {meeting.has_recording && (
          <button className="primary" onClick={onTranscribe}>
            Расшифровать
          </button>
        )}
      </div>
    );
  }

  const q = query.trim().toLowerCase();
  const segments = q
    ? t.segments.filter(
        (s) =>
          s.text.toLowerCase().includes(q) || s.speaker.toLowerCase().includes(q),
      )
    : t.segments;

  return (
    <>
      <div className="card-body" style={{ paddingBottom: 8 }}>
        <div className="row">
          <input
            placeholder="Поиск по расшифровке"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ flex: 1 }}
          />
          <a className="btn sm" href={`/api/meetings/${meeting.id}/transcript.txt`} download>
            .txt
          </a>
          <a className="btn sm" href={`/api/meetings/${meeting.id}/transcript.vtt`} download>
            .vtt
          </a>
        </div>
        <div className="muted small" style={{ marginTop: 8 }}>
          Источник: {t.provider} · {t.word_count} слов · {t.segments.length} реплик
          {q && ` · найдено ${segments.length}`}
        </div>
      </div>

      <div className="transcript">
        {segments.map((s, i) => (
          <div className="seg" key={i}>
            <button
              className="ts"
              onClick={() => hasVideo && onSeek(s.start)}
              title={hasVideo ? "Перейти к этому месту записи" : "Записи нет"}
              disabled={!hasVideo}
            >
              {fmtClock(s.start)}
            </button>
            <div style={{ flex: 1 }}>
              {s.speaker && <span className="who">{s.speaker}: </span>}
              <span>{s.text}</span>
            </div>
          </div>
        ))}
        {segments.length === 0 && <div className="empty small">Ничего не нашлось</div>}
      </div>
    </>
  );
}

function LogTab({ meeting }: { meeting: MeetingDetail }) {
  return (
    <div className="card-body">
      <h3>Шаги обработки</h3>
      {meeting.pipeline.length === 0 ? (
        <p className="muted small">Задач по этой встрече ещё не было.</p>
      ) : (
        <ul className="list-clean small">
          {meeting.pipeline.map((j) => (
            <li key={j.id} className="row">
              <span
                className={`badge ${
                  j.status === "done"
                    ? "green"
                    : j.status === "failed"
                      ? "red"
                      : j.status === "running"
                        ? "blue"
                        : "amber"
                }`}
              >
                {j.status}
              </span>
              <span style={{ flex: 1 }}>
                {jobLabel(j.type)}
                {j.attempts > 1 && (
                  <span className="muted"> · попыток: {j.attempts}</span>
                )}
                {j.last_error && (
                  <div className="mono muted" style={{ wordBreak: "break-word" }}>
                    {j.last_error}
                  </div>
                )}
              </span>
              <span className="muted nowrap">{fmtTime(j.updated_at)}</span>
            </li>
          ))}
        </ul>
      )}

      <h3 style={{ marginTop: 20 }}>Хроника</h3>
      {meeting.timeline.length === 0 ? (
        <p className="muted small">Событий пока нет.</p>
      ) : (
        <ul className="timeline">
          {meeting.timeline.map((e) => (
            <li key={e.id}>
              <div className="when">
                {new Date(e.created_at).toLocaleString("ru-RU")} · {e.type}
              </div>
              {e.message && <div>{e.message}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Загрузка записи очной встречи. Файл уходит на сервер как есть — там его
 * подхватывает тот же конвейер, что и записи из Zoom.
 */
function UploadCard({
  meeting,
  onUploaded,
  onError,
}: {
  meeting: MeetingDetail;
  onUploaded: (m: MeetingDetail) => void;
  onError: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  const send = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    onError("");
    try {
      onUploaded(await api.uploadAudio(meeting.id, file));
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="empty">
        <h3>Запись ещё не загружена</h3>
        <p>
          Очная встреча — запись приносите сами. Перетащите файл сюда или выберите
          его на компьютере. Подойдут mp3, m4a, wav, ogg — до 25 МБ.
        </p>

        <label className="btn btn-primary" style={{ cursor: "pointer" }}>
          {busy ? <span className="spinner" /> : <IconUpload />}
          {busy ? " Загружаю…" : " Выбрать файл"}
          <input
            type="file"
            accept="audio/*,video/mp4"
            style={{ display: "none" }}
            disabled={busy}
            onChange={(e) => send(e.target.files?.[0])}
          />
        </label>

        <div className="muted small" style={{ marginTop: 14 }}>
          Проще всего — прислать запись боту в телеграм: карточка соберётся сама
          и вернётся в чат.
        </div>
      </div>
    </div>
  );
}
